/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Breadcrumbs,
  Box,
  Checkbox,
  Popover,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import moment from "moment";
import {
  ArchivedNote,
  BulkActionContainer,
  BulkActiveButton,
  BulkInactiveButton,
  ContentContainer,
  DecisionContainer,
  DecisionMenuList,
  InactivityReviewPage,
  SelectionCount,
  StatusContainer,
  StyledDecisionButton,
  brandLogoStyle,
  decisionMenuItemStyle,
  decisionPopoverPaperStyle,
  userButtonContainerStyle,
} from "./styles";
import {
  StyledHeaderContainer,
  NavbarActions,
  StyledUserButton,
  StyledAccountIcon,
  StyledMenu,
} from "../../no_permissions/styles";
import { StyledBreadCrumbContainer, StyledLink } from "../../../components/navigable_breadcrumb_link/styles";
import brandLogo from "../../../assets/images/OT_only_name.png";
import { MenuHeading } from "../../../components/menu_heading";
import { IndexTable } from "../../../components/index_table";
import { StyledMenuItem } from "../../../components/styled_menu_item";
import { ConfirmationDialog } from "../../../components/confirmation_dialog";
import { Loader } from "../../../components/loader";
import { LoadingSpinner } from "../../../components/loading_spinner";
import { useNotificationContext } from "../../../contexts/notificationContext";
import { useOtMutation } from "../../../hooks/mutations/useOtMutation";
import { policyEngineMutationKeyGroups } from "../../../hooks/mutations/queryKeyGroups/policyEngineMutationKeys";
import { getMutationErrorMessage } from "../../../hooks/mutations/utils/mutationErrorMessage";
import { Query_Keys } from "../../../lib/queryKeys";
import api from "../../../api";
import authManager from "../../../utils/authManager";
import camelize from "../../../utils/camelize";

const DECISION_OPTIONS = [
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
];

const DECISION_FALLBACK_ERROR = "Failed to record the decision";
const REVIEW_FALLBACK_ERROR = "We could not load this review. Please refresh to try again.";

// The read model reports decisions as the event type that recorded them, not as
// the `active`/`inactive` value the write side takes.
const EVENT_LABELS = {
  confirmed_active: "Active",
  confirmed_inactive: "Inactive",
  auto_expired: "Expired",
};

const EVENT_TONES = {
  confirmed_active: "active",
  confirmed_inactive: "inactive",
  auto_expired: "expired",
};

const STATUS_LABELS = {
  pending: "Pending",
  recovered: "Recovered",
  decided: "Decided",
  awaiting_next_cycle: "Awaiting next cycle",
};

const STATUS_HINTS = {
  pending: "Flagged this cycle and still awaiting a decision.",
  recovered: "Flagged, but has shown activity since — archiving them would be a mistake.",
  decided: "A decision has already been recorded for this cycle.",
  awaiting_next_cycle: "Looks inactive now, but was not flagged this cycle, so no decision can be filed yet.",
};

/**
 * @description Turns one entry of the stored detection payload into a readable
 * phrase. Each key is a check that found no sign of life; `value` is the most
 * recent activity found outside the window, so a nil value means "never".
 * @param check The rule key of the inactivity check.
 * @param detail The stored payload for that check.
 * @returns The phrase to show, or null for an unrecognised key.
 */
const describeReason = (check, detail) => {
  const since = detail?.value ? moment.parseZone(detail.value).format("MMM D, YYYY") : null;

  switch (check) {
    case "last_login":
      return since ? `No login since ${since}` : "Never logged in";
    case "active_discount_card":
      return "No active perq card";
    case "check_in":
      return since ? `No check-in since ${since}` : "No check-ins recorded";
    default:
      return null;
  }
};

/**
 * @description Every reason a user was flagged. An empty payload is not an empty
 * reason list: `awaiting_next_cycle` rows have no stored `flagged` event at all,
 * because the cycle that would carry one has not opened yet.
 * @param reasons The entry's reasons payload.
 * @returns The phrases to render.
 */
const buildReasonList = (reasons) => {
  const entries = Object.entries(reasons || {});

  if (!entries.length) return ["Newly inactive"];

  return entries.map(([check, detail]) => describeReason(check, detail)).filter(Boolean);
};

const formatTimestamp = (value) => {
  if (!value) return "-";

  const parsed = moment.parseZone(value);
  return parsed.isValid() ? parsed.format("MM/DD/YYYY h:mm A") : "-";
};

/**
 * @description Summarises a per-row decision response. The endpoint applies each
 * row independently, so a batch can come back part archived, part recorded and
 * part failed — reporting it as one outcome would hide the rows that did land.
 * @param results The `results` array from the decisions endpoint.
 * @returns A success sentence, an error sentence, or both.
 */
const summarizeDecisionResults = (results) => {
  const rows = Array.isArray(results) ? results : [];
  const archived = rows.filter((row) => row?.status === "archived").length;
  const recorded = rows.filter((row) => row?.status === "recorded").length;
  const failed = rows.filter((row) => row?.status === "failed");

  const applied = [
    archived ? `${archived} archived` : null,
    recorded ? `${recorded} marked active` : null,
  ].filter(Boolean);

  // The realistic failure is another admin having settled that row first, so its
  // own message is worth more to the reader than a generic count.
  const failureMessages = [...new Set(failed.flatMap((row) => row?.errors || []))].filter(Boolean);

  return {
    success: applied.length ? applied.join(", ") : "",
    failure: failed.length
      ? `${failed.length} could not be updated${failureMessages.length ? `: ${failureMessages.join(". ")}` : ""}`
      : "",
  };
};

export const InactivityReview = () => {
  const [searchParams] = useSearchParams();
  const restaurantId = searchParams.get("restaurant_id") || "";
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [selectedIds, setSelectedIds] = useState([]);
  const [decisionMenuAnchorEl, setDecisionMenuAnchorEl] = useState(null);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [rowDecisionLoadingId, setRowDecisionLoadingId] = useState(null);
  const [pendingBulkDecision, setPendingBulkDecision] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [loggingOut, setLoggingOut] = useState(false);
  const [admin, setAdmin] = useState({});
  const showNotification = useNotificationContext();

  // Read here rather than from the store: `fetchAdmin` is dispatched by Layout,
  // which this page deliberately does not render.
  useEffect(() => {
    let active = true;

    api.get("/v1/admin/me")
      .then((response) => {
        if (active) setAdmin(response?.data || {});
      })
      .catch(() => {
        if (active) setAdmin({});
      });

    return () => {
      active = false;
    };
  }, []);

  const fetchInactivityReview = async () => {
    const response = await api.get("/v1/admin/policy_engines/inactivity_review", {
      params: { restaurant_id: restaurantId, page, per_page: itemsPerPage },
    });

    return { data: response?.data, pageCount: response?.headers["total-count"] };
  };

  // Deliberately uncached: a decision another admin filed a moment ago changes
  // which rows are still actionable, and a stale list invites a doomed submit.
  const { data: review, isFetching, isError, error: reviewError } = useQuery({
    queryKey: Query_Keys.inactivity_review({ restaurantId, page, itemsPerPage }),
    queryFn: fetchInactivityReview,
    enabled: Boolean(restaurantId),
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
  });

  const entries = review?.data?.entries || [];
  const reviewHasRun = Boolean(review?.data?.card_policy_id && review?.data?.cycle_started_at);
  const actionableIds = useMemo(
    () => entries.filter((entry) => entry?.actionable).map((entry) => entry.network_guest_id),
    [entries]
  );

  const { mutateAsync: recordDecisions } = useOtMutation({
    mutationFn: async (payload) => await api.post("/v1/admin/policy_engines/inactivity_decisions", payload),
    invalidateKeys: policyEngineMutationKeyGroups.inactivityDecisions,
    // The whole-batch 422s ("no review is open") read as full sentences already, so
    // they are surfaced verbatim rather than replaced by a generic failure.
    errorMessage: ({ error }) => getMutationErrorMessage(error, DECISION_FALLBACK_ERROR),
    onSuccess: (response) => {
      const { success, failure } = summarizeDecisionResults(response?.data?.results);

      if (success) showNotification(success, "success");
      if (failure) showNotification(failure, "error");
    },
  });

  const submitDecisions = async (guestIds, decision) => {
    if (!guestIds.length) return;

    await recordDecisions({
      restaurant_id: restaurantId,
      decisions: guestIds.map((networkGuestId) => ({ network_guest_id: networkGuestId, decision })),
    });
  };

  const handleUserMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleUserMenuClose = () => setAnchorEl(null);

  const handleLogOut = async () => {
    setLoggingOut(true);
    authManager.logout(true);
    showNotification("Logged out successfully", "success");
    setAnchorEl(null);
    setLoggingOut(false);
  };

  const handleSelect = (networkGuestId) => {
    setSelectedIds((previous) => (
      previous.includes(networkGuestId)
        ? previous.filter((id) => id !== networkGuestId)
        : [...previous, networkGuestId]
    ));
  };

  // Select-all covers the actionable rows only, so it can never queue a row the
  // endpoint would refuse.
  const handleSelectAll = () => {
    setSelectedIds((previous) => (previous.length === actionableIds.length ? [] : actionableIds));
  };

  const handleDecisionMenuOpen = (event, entry) => {
    if (rowDecisionLoadingId) return;

    setDecisionMenuAnchorEl(event.currentTarget);
    setSelectedEntry(entry);
  };

  const handleDecisionMenuClose = () => {
    if (rowDecisionLoadingId) return;

    setDecisionMenuAnchorEl(null);
    setSelectedEntry(null);
  };

  const handleRowDecision = async (entry, decision) => {
    if (!entry?.network_guest_id) return;

    setRowDecisionLoadingId(entry.network_guest_id);

    try {
      await submitDecisions([entry.network_guest_id], decision);
      setSelectedIds((previous) => previous.filter((id) => id !== entry.network_guest_id));
    } catch {
      // Reported by useOtMutation's error handler.
    } finally {
      setRowDecisionLoadingId(null);
      setDecisionMenuAnchorEl(null);
      setSelectedEntry(null);
    }
  };

  const handleBulkDecisionConfirm = async () => {
    if (!pendingBulkDecision) return;

    setBulkLoading(true);

    try {
      await submitDecisions(selectedIds, pendingBulkDecision);
      setSelectedIds([]);
    } catch {
      // Reported by useOtMutation's error handler.
    } finally {
      setBulkLoading(false);
      setPendingBulkDecision(null);
    }
  };

  const columnOptions = [
    { key: "select", label: "", sortable: false, width: 60 },
    { key: "name", sortable: false, width: 180 },
    { key: "email", sortable: false, width: 220 },
    { key: "status", sortable: false, width: 170 },
    { key: "reasons", label: "Why Flagged", sortable: false, width: 260 },
    { key: "still_inactive", label: "Still Inactive", sortable: false, width: 120 },
    { key: "flagged_at", sortable: false, width: 180 },
    { key: "guest_response", label: "User Response", sortable: false, width: 150 },
    { key: "decision", sortable: false, width: 150 },
    { key: "decided_by", sortable: false, width: 150 },
  ];

  const columns = columnOptions.map((column) => ({
    ...column,
    label: column.key === "select"
      ? (
        <Checkbox
          size="small"
          disabled={actionableIds.length === 0 || bulkLoading}
          indeterminate={selectedIds.length > 0 && selectedIds.length < actionableIds.length}
          checked={actionableIds.length > 0 && selectedIds.length === actionableIds.length}
          onChange={handleSelectAll}
          inputProps={{ "aria-label": "Select all actionable users" }}
        />
      )
      : column.label ?? camelize(column.key),
  }));

  const rows = entries.map((entry) => {
    const reasonList = buildReasonList(entry?.reasons);
    const isRowLoading = rowDecisionLoadingId === entry?.network_guest_id;

    return {
      id: entry?.network_guest_id,
      select: {
        display: (
          <Tooltip title={entry?.actionable ? "" : "No decision can be recorded for this user"} arrow>
            <span>
              <Checkbox
                size="small"
                disabled={!entry?.actionable || bulkLoading || isRowLoading}
                checked={selectedIds.includes(entry?.network_guest_id)}
                onChange={() => handleSelect(entry?.network_guest_id)}
                inputProps={{ "aria-label": `Select ${entry?.first_name || "user"}` }}
              />
            </span>
          </Tooltip>
        ),
      },
      name: [entry?.first_name, entry?.last_name].filter(Boolean).join(" ") || "-",
      email: entry?.email || "-",
      status: {
        display: (
          <Tooltip title={STATUS_HINTS[entry?.status] || ""} arrow>
            <StatusContainer status={entry?.status}>
              {STATUS_LABELS[entry?.status] || camelize(entry?.status || "")}
            </StatusContainer>
          </Tooltip>
        ),
      },
      reasons: {
        display: (
          <Tooltip title={reasonList.join(" · ")} arrow>
            <span>{reasonList.join(", ")}</span>
          </Tooltip>
        ),
      },
      still_inactive: entry?.still_inactive ? "Yes" : "No",
      flagged_at: formatTimestamp(entry?.flagged_at),
      guest_response: {
        display: entry?.guest_response
          ? (
            <Tooltip title={`Answered ${formatTimestamp(entry?.guest_responded_at)}`} arrow>
              <DecisionContainer decision={EVENT_TONES[entry.guest_response]}>
                {EVENT_LABELS[entry.guest_response] || camelize(entry.guest_response)}
              </DecisionContainer>
            </Tooltip>
          )
          : "-",
      },
      decision: {
        display: isRowLoading ? (
          <LoadingSpinner isLoading={true} />
        ) : entry?.actionable ? (
          <StyledDecisionButton
            disableRipple
            endIcon={<KeyboardArrowDownIcon sx={{ color: "grey" }} />}
            onClick={(event) => handleDecisionMenuOpen(event, entry)}
            data-testid={`inactivity-decision-button-${entry?.network_guest_id}`}
          >
            <DecisionContainer decision={EVENT_TONES[entry?.decision]}>
              {EVENT_LABELS[entry?.decision] || "Decide"}
            </DecisionContainer>
          </StyledDecisionButton>
        ) : (
          <Box>
            <DecisionContainer decision={EVENT_TONES[entry?.decision]}>
              {EVENT_LABELS[entry?.decision] || "-"}
            </DecisionContainer>
            {/* Archiving has no inverse anywhere in the backend, so the row says so
                instead of offering a revert that cannot work. */}
            {entry?.archived && <ArchivedNote>Archived · cannot be undone</ArchivedNote>}
          </Box>
        ),
      },
      decided_by: entry?.decided_by || "-",
    };
  });

  const decisionMenuOpen = Boolean(decisionMenuAnchorEl);

  const renderBody = () => {
    if (!restaurantId) {
      return <Alert severity="info">Open this page from the review email so it knows which restaurant to load.</Alert>;
    }

    if (isFetching) {
      return <Loader />;
    }

    if (isError) {
      return <Alert severity="error">{getMutationErrorMessage(reviewError, REVIEW_FALLBACK_ERROR)}</Alert>;
    }

    if (!reviewHasRun) {
      return <Alert severity="info">No inactivity review has run yet for this restaurant.</Alert>;
    }

    return (
      <React.Fragment>
        <BulkActionContainer>
          <SelectionCount>
            {selectedIds.length > 0
              ? `${selectedIds.length} selected`
              : "Select the users you have checked to record a decision"}
          </SelectionCount>

          <BulkActiveButton
            variant="outlined"
            disabled={selectedIds.length === 0 || bulkLoading}
            onClick={() => setPendingBulkDecision("active")}
            data-testid="bulk-mark-active-btn"
          >
            Mark Active
          </BulkActiveButton>

          <BulkInactiveButton
            variant="outlined"
            disabled={selectedIds.length === 0 || bulkLoading}
            onClick={() => setPendingBulkDecision("inactive")}
            data-testid="bulk-mark-inactive-btn"
          >
            Mark Inactive
          </BulkInactiveButton>
        </BulkActionContainer>

        <IndexTable
          columns={columns}
          data={rows}
          tableId="inactivity-review"
          type="entries"
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          page={page}
          setPage={setPage}
          pageCount={review?.pageCount}
          report={false}
          emptyStateText="No users need review at this restaurant"
        />
      </React.Fragment>
    );
  };

  return (
    <InactivityReviewPage>
      <StyledHeaderContainer>
        <img src={brandLogo} alt="Brand Logo" style={brandLogoStyle} />
        <NavbarActions className="header-info" elevation={0}>
          <div style={userButtonContainerStyle}>
            <StyledUserButton startIcon={<StyledAccountIcon />} onClick={handleUserMenuOpen}>
              {admin?.first_name}
            </StyledUserButton>
            <StyledMenu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleUserMenuClose}>
              <StyledMenuItem onClick={handleLogOut}>
                {loggingOut ? <LoadingSpinner isLoading={true} /> : "Log out"}
              </StyledMenuItem>
            </StyledMenu>
          </div>
        </NavbarActions>
      </StyledHeaderContainer>

      <StyledBreadCrumbContainer>
        <Breadcrumbs>
          <StyledLink color={"#515152"}>Home</StyledLink>
        </Breadcrumbs>
      </StyledBreadCrumbContainer>

      <ContentContainer>
        <MenuHeading report={false} dataTestId={"page-title"}>Needs Review</MenuHeading>

        {renderBody()}
      </ContentContainer>

      <Popover
        open={decisionMenuOpen}
        anchorEl={decisionMenuAnchorEl}
        onClose={handleDecisionMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ sx: decisionPopoverPaperStyle }}
      >
        <DecisionMenuList dense disablePadding>
          {DECISION_OPTIONS.map((option) => (
            <StyledMenuItem
              key={option.value}
              disabled={rowDecisionLoadingId === selectedEntry?.network_guest_id}
              onClick={() => handleRowDecision(selectedEntry, option.value)}
              sx={decisionMenuItemStyle}
            >
              {option.label}
            </StyledMenuItem>
          ))}
        </DecisionMenuList>
      </Popover>

      <ConfirmationDialog
        open={Boolean(pendingBulkDecision)}
        onClose={() => setPendingBulkDecision(null)}
        Icon={CloseIcon}
        dialogTitle={pendingBulkDecision === "inactive" ? "Mark these users inactive?" : "Mark these users active?"}
        dialogBody={
          pendingBulkDecision === "inactive"
            ? `This archives ${selectedIds.length} ${selectedIds.length === 1 ? "user" : "users"} at this restaurant and revokes their perq cards. It cannot be undone from this page.`
            : `This records ${selectedIds.length} ${selectedIds.length === 1 ? "user" : "users"} at this restaurant as still active.`
        }
        confirmationText={pendingBulkDecision === "inactive" ? "Yes, I want to mark them inactive" : "Yes, I want to mark them active"}
        handleConfirm={handleBulkDecisionConfirm}
        isLoading={bulkLoading}
        dataTestId="bulk-decision-confirmation-dialog"
      />
    </InactivityReviewPage>
  );
};
